import { useEffect } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { LanguageProvider } from '@/i18n/LanguageProvider';

/**
 * On route change (pathname), scroll the window to top.
 * On hash change with no pathname change, smooth-scroll to the targeted anchor.
 * React Router v6 doesn't do this by default, so without this each page
 * keeps the previous page's scroll position (jarring).
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el) {
        requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);
  return null;
}

import Footer from '@/components/Footer';
import Header from '@/components/Header';

// V2 (current site)
import V2Page from '@/app/v2/page';
import V2About from '@/app/v2/about/page';
import V2EventsList from '@/app/v2/events/page';
import V2EventDetail from '@/app/v2/events/[id]/page';
import V2GraviLens from '@/app/v2/GraviLens/page';
import V2GraviLensHandbook from '@/app/v2/GraviLens/handbook/page';
import V2Qiaodong from '@/app/v2/Qiaodong/page';
import V2Kontakt from '@/app/v2/kontakt/page';
import V2Impressum from '@/app/v2/impressum/page';
import V2Datenschutz from '@/app/v2/datenschutz/page';
import V2Donate from '@/app/v2/donate/page';
import V2Terms from '@/app/v2/legal/terms/page';

// V3 (paper) redesign — preview only, mounted under /next/*
import V3Page from '@/app/v3/page';

// V1 archive — only the routes that have NO V2 equivalent are still wired.
// Everything else lives in src/archive/app/ for reference but is no longer routed.
import BPFPage from '@/archive/app/Qiaodong/BPF/page';
import BPFWorkshopPage from '@/archive/app/Qiaodong/BPF/workshop/page';
import BPFWorkshopGroupPage from '@/archive/app/Qiaodong/BPF/workshop/[groupId]/page';
import BPFWorkshopPoetPage from '@/archive/app/Qiaodong/BPF/workshop/[groupId]/[poetName]/page';
import BPFEventsPage from '@/archive/app/Qiaodong/BPF/events/page';
import BPFEventDetailPage from '@/archive/app/Qiaodong/BPF/events/[eventId]/page';
import BPFGuestPage from '@/archive/app/Qiaodong/BPF/events/[eventId]/[guestId]/page';
import ChinaPoetryPage from '@/archive/app/Qiaodong/BPF/china-poetry/page';
import ChinaPoetrySectionPage from '@/archive/app/Qiaodong/BPF/china-poetry/[id]/page';
import ChinaPoetryPoetPage from '@/archive/app/Qiaodong/BPF/china-poetry/[id]/[poest]/page';
import GraviLensBridgePage from '@/archive/app/GraviLens/bridge/page';
import LegalTermsEn from '@/archive/app/legal/terms_en/page';
import LegalImpressumEn from '@/archive/app/legal/impressum_en/page';
import LegalPrivacyEn from '@/archive/app/legal/privacy_policy_gdpr_en/page';

// V1-archive routes still served (no V2 equivalent). These wear the V1 Header/Footer.
const V1_ARCHIVE_PREFIXES = [
  '/Qiaodong/BPF',
  '/GraviLens/bridge',
  '/legal/terms_en',
  '/legal/impressum_en',
  '/legal/privacy_policy_gdpr_en',
];

function isV1ArchivePath(pathname: string): boolean {
  return V1_ARCHIVE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function App() {
  const location = useLocation();
  const showV1Chrome = isV1ArchivePath(location.pathname);
  return (
    <LanguageProvider>
      <ScrollToTop />
      <div className="flex min-h-screen flex-col">
        {showV1Chrome && <Header />}
        <main className="flex-grow">
          <Routes>
            {/* V3 — paper redesign preview (does not affect live routes) */}
            <Route path="/next" element={<V3Page />} />

            {/* V2 — top-level (canonical) */}
            <Route path="/" element={<V2Page />} />
            <Route path="/about" element={<V2About />} />
            <Route path="/events" element={<V2EventsList />} />
            <Route path="/events/:id" element={<V2EventDetail />} />
            <Route path="/donate" element={<V2Donate />} />
            <Route path="/kontakt" element={<V2Kontakt />} />
            <Route path="/impressum" element={<V2Impressum />} />
            <Route path="/datenschutz" element={<V2Datenschutz />} />
            <Route path="/GraviLens" element={<V2GraviLens />} />
            <Route path="/GraviLens/handbook" element={<V2GraviLensHandbook />} />
            <Route path="/Qiaodong" element={<V2Qiaodong />} />
            <Route path="/legal/terms" element={<V2Terms />} />
            {/* /team → /about (team info lives on V2 about page) */}
            <Route path="/team" element={<Navigate to="/about" replace />} />

            {/* V2 — /v2/* aliases (kept for backward compat / external links) */}
            <Route path="/v2" element={<V2Page />} />
            <Route path="/v2/about" element={<V2About />} />
            <Route path="/v2/events" element={<V2EventsList />} />
            <Route path="/v2/events/:id" element={<V2EventDetail />} />
            <Route path="/v2/GraviLens" element={<V2GraviLens />} />
            <Route path="/v2/GraviLens/handbook" element={<V2GraviLensHandbook />} />
            <Route path="/v2/Qiaodong" element={<V2Qiaodong />} />
            <Route path="/v2/kontakt" element={<V2Kontakt />} />
            <Route path="/v2/impressum" element={<V2Impressum />} />
            <Route path="/v2/datenschutz" element={<V2Datenschutz />} />
            <Route path="/v2/donate" element={<V2Donate />} />
            <Route path="/v2/legal/terms" element={<V2Terms />} />

            {/* V1 archive — content has no V2 equivalent yet */}
            <Route path="/Qiaodong/BPF" element={<BPFPage />} />
            <Route path="/Qiaodong/BPF/workshop" element={<BPFWorkshopPage />} />
            <Route path="/Qiaodong/BPF/workshop/:groupId" element={<BPFWorkshopGroupPage />} />
            <Route path="/Qiaodong/BPF/workshop/:groupId/:poetName" element={<BPFWorkshopPoetPage />} />
            <Route path="/Qiaodong/BPF/events" element={<BPFEventsPage />} />
            <Route path="/Qiaodong/BPF/events/:eventId" element={<BPFEventDetailPage />} />
            <Route path="/Qiaodong/BPF/events/:eventId/:guestId" element={<BPFGuestPage />} />
            <Route path="/Qiaodong/BPF/china-poetry" element={<ChinaPoetryPage />} />
            <Route path="/Qiaodong/BPF/china-poetry/:id" element={<ChinaPoetrySectionPage />} />
            <Route path="/Qiaodong/BPF/china-poetry/:id/:poest" element={<ChinaPoetryPoetPage />} />
            <Route path="/GraviLens/bridge" element={<GraviLensBridgePage />} />
            <Route path="/legal/terms_en" element={<LegalTermsEn />} />
            <Route path="/legal/impressum_en" element={<LegalImpressumEn />} />
            <Route path="/legal/privacy_policy_gdpr_en" element={<LegalPrivacyEn />} />
          </Routes>
        </main>
        {showV1Chrome && <Footer />}
      </div>
    </LanguageProvider>
  );
}
